import React from 'react';

import { Box, Text } from '@betterment/samba-react';

import { PurposeSection } from './PurposeSection';

import { SambaAutoLink } from '../../../features/navigation/SambaAutoLink';
import { ModalSection } from '../../modal-section/ModalSection';
import { ModalSectionRow } from '../../modal-section/ModalSectionRow';
import { formatLegalAccountType } from '../helpers';

import type { AccountSettingsUrlBuilders, RenderCloseAccountAction, RenderDeletePurposeAction } from '../contentTypes';
import type { CashAccount } from '../data';

type CashAccountSettingsProps = {
  account: CashAccount;
  currentPath: string;
  urlBuilders: Pick<AccountSettingsUrlBuilders, 'accountDetails' | 'editPortfolio' | 'legalAccounts' | 'editPurpose'>;
  renderDeletePurpose?: RenderDeletePurposeAction;
  renderCloseAccount?: RenderCloseAccountAction;
  purposelessCopy?: string;
};

export const CashAccountSettings: React.FC<CashAccountSettingsProps> = ({
  account,
  currentPath,
  urlBuilders,
  renderDeletePurpose,
  renderCloseAccount,
  purposelessCopy,
}) => {
  const { legalAccount } = account;
  const legalAccountType = formatLegalAccountType(legalAccount);
  const hasBeneficiaries = legalAccount.primaryBeneficiaries.length > 0;

  const closeAccountAction =
    renderCloseAccount &&
    renderCloseAccount({
      balance: account.balance,
      purposeName: account.envelope.purpose?.name,
      name: account.name,
      legacyAccountId: account.legacySubAccountId,
    });

  return (
    <Box css={{ display: 'grid', gap: 'space.200' }}>
      <ModalSection header="Account details" cta={urlBuilders.accountDetails}>
        <ModalSectionRow>
          <ModalSectionRow.LeadingContent>
            <Text>Account type</Text>
          </ModalSectionRow.LeadingContent>
          <ModalSectionRow.TrailingContent>
            <Text>{legalAccountType}</Text>
          </ModalSectionRow.TrailingContent>
        </ModalSectionRow>
        {!hasBeneficiaries && urlBuilders.legalAccounts && (
          <ModalSectionRow>
            <ModalSectionRow.LeadingContent>
              <Text>Beneficiaries</Text>
            </ModalSectionRow.LeadingContent>
            <ModalSectionRow.TrailingContent>
              <SambaAutoLink to={urlBuilders.legalAccounts}>Add beneficiary</SambaAutoLink>
            </ModalSectionRow.TrailingContent>
          </ModalSectionRow>
        )}
      </ModalSection>
      <PurposeSection
        account={account}
        currentPath={currentPath}
        urlBuilders={{ editPurpose: urlBuilders.editPurpose }}
        renderDeletePurpose={renderDeletePurpose}
        purposelessCopy={purposelessCopy}
      />
      {closeAccountAction}
    </Box>
  );
};
